import classes from "./Studio.module.css"
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { DatePickerWrapper } from "./Studio";
import 'react-datepicker/dist/react-datepicker.css'

import { startOfDay, endOfDay, isSameDay, isBefore, startOfToday, isToday } from "date-fns";
import { useBooking } from "../../store/bookingStore";
import { de } from "date-fns/locale";
import {
  apiDayToJsDay,
  getBookingDurationHours,
  formatLocalDate,
  getBlockedDateIntervals as getStudioBlockedDateIntervals,
  hasBusyIntervalOnDate,
  hasBookableTimeSlot,
  isDateFullyBlocked as isStudioDateFullyBlocked,
} from "../../util/studioAvailability";

// Configure DatePicker to use German locale so weeks start on Monday
registerLocale('de', de);
setDefaultLocale('de');

const AvailabilityFooter = ({
    showDatePicker, 
    handleCloseDatePicker, 
    bookingStudioData,
    handleAvailabilityDateChange,
    availabilityUnavailable = false,
    availabilityUnavailableMessage = "Verfügbarkeit konnte gerade nicht geladen werden.",
    availabilityUnavailableActionLabel = "Erneut versuchen",
    onRetryAvailability,
	}) => {
  const { bookingData, updateBookingData } = useBooking();
  const bookingDurationHours = getBookingDurationHours(bookingStudioData, bookingData);

  // Handle date change and update booking data
  const handleDateChange = (date) => {
    if (!filterDate(date)) return;

    const formattedDate = formatLocalDate(date);
    updateBookingData('date', formattedDate);
    handleAvailabilityDateChange(date);
  };
  
  const getBlockedDateIntervals = () => getStudioBlockedDateIntervals(bookingStudioData);
  
  // Helper function to check if a date is fully blocked
  const isDateFullyBlocked = (date) => isStudioDateFullyBlocked(date, bookingStudioData);
  
  // Determine if a date is partially available/booked
  const isPartiallyAvailable = (date) => {
    if (!bookingStudioData) return false;
    
    // Don't show indicators for past dates (except today)
    if (isBefore(date, startOfToday()) && !isToday(date)) return false;
    
    // First check if the date is fully blocked by blocked intervals
    // If it is fully blocked, don't show partial availability indicator
    if (isDateFullyBlocked(date)) {
      return false;
    }
    
    // Check if day has partial availability restrictions
    const jsDay = date.getDay();
    const hasPartialAvailability = bookingStudioData.availabilities?.items?.some(item => 
      apiDayToJsDay(item.dayOfWeek) === jsDay && 
      (item.startTime !== "00:00" || item.endTime !== "23:59")
    );
    
    // Check if day is the start or end day of a partial block period
    const isPartialBlockPeriodDay = bookingStudioData.blockedTimes?.items?.some(item => {
      if (item.isFullDay) return false;
      
      const startDate = new Date(item.startDateTime);
      const endDate = new Date(item.endDateTime);
      
      // Only the start day and end day of a partial block period are partially available
      return isSameDay(date, startDate) || isSameDay(date, endDate);
    });
    
    // Check if day has bookings
    const hasBookings = hasBusyIntervalOnDate(date, bookingStudioData);
    
    return hasPartialAvailability || isPartialBlockPeriodDay || hasBookings;
  };
  
  // Override minDate if today is unavailable
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (!filterDate(new Date())) {
      return tomorrow; // Use tomorrow as min date if today is unavailable
    }
    
    return new Date(); // Otherwise use today
  };
  
  // Filter out days that do not have a bookable slot for the current booking duration.
  const filterDate = (date) => {
    return hasBookableTimeSlot(date, bookingStudioData, {
      durationHours: bookingDurationHours,
    });
  };
  
  // Get blocked intervals with a special check for today
  const getCombinedBlockedIntervals = () => {
    const intervals = getBlockedDateIntervals();
    
    // If today is unavailable but not caught by other filters, explicitly add it
    if (isToday(new Date()) && !filterDate(new Date())) {
      const today = new Date();
      intervals.push({
        start: startOfDay(today),
        end: endOfDay(today)
      });
    }
    
    return intervals;
  };
  
  // Custom day content renderer
  const renderDayContents = (day, date) => {
    const isPartial = isPartiallyAvailable(date);
    
    return (
      <div className={classes.dayContainer}>
        <span>{day}</span>
        {isPartial && (
          <div className={classes.partialIndicator}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="5" viewBox="0 0 18 5" fill="none">
              <rect x="0.5" y="1.41602" width="17" height="3" rx="1.5" stroke="#B1B6BB"/>
              <rect x="0.5" y="1.41602" width="5" height="3" rx="1.5" fill="#B1B6BB" stroke="#B1B6BB"/>
            </svg>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${classes.dateContainer} ${showDatePicker ? classes.show : classes.hide}`}>
      <div className={classes.dateHeaderContainer}>
        <span style={{opacity: 0 }}></span>
        <h3 className="mediumLarge">Verfügbarkeit zeigen</h3>
        <div className={classes.closeIconContainer} onClick={handleCloseDatePicker}>
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
          </svg>
        </div>
      </div>
      <div className={classes.availabilityContainer}>
        {availabilityUnavailable ? (
          <div className={classes.availabilityUnavailable}>
            <p className="regularMiddle">{availabilityUnavailableMessage}</p>
            {onRetryAvailability && (
              <button
                type="button"
                className={`${classes.availabilityRetryButton} mediumMiddle`}
                onClick={onRetryAvailability}
              >
                {availabilityUnavailableActionLabel}
              </button>
            )}
          </div>
        ) : (
          <DatePickerWrapper>
            <DatePicker
              // selected={null}
              onChange={handleDateChange}
              formatWeekDay={() => ""}
              // dayClassName={getDayClassName}
              dateFormat="dd.MM.yyyy"
              inline
              locale="de"
              minDate={getMinDate()}
              excludeDateIntervals={getCombinedBlockedIntervals()}
              filterDate={filterDate}
              renderDayContents={renderDayContents}
              // calendarClassName={classes.noSelectCalendar}
            />
          </DatePickerWrapper>
        )}
      </div>
    </div>
  )
}

export default AvailabilityFooter;
